import { useEffect, useState } from "react";
import { Automations } from "./components/Automations";
import { Cases } from "./components/Cases";
import { FinalCTA } from "./components/FinalCTA";
import { Fonio } from "./components/Fonio";
import { Footer } from "./components/Footer";
import { Hero } from "./components/Hero";
import { Industries } from "./components/Industries";
import { Integrations } from "./components/Integrations";
import { Nav } from "./components/Nav";
import { Products } from "./components/Products";
import { ROI } from "./components/ROI";
import { TweaksPanel } from "./components/TweaksPanel";
import { COPY, type Lang } from "./lib/copy";
import {
  loadTweaks,
  saveTweaks,
  type TweakKey,
  type TweakValue,
  type Tweaks,
} from "./lib/tweaks";

export function App() {
  const [tweaks, setTweaks] = useState<Tweaks>(loadTweaks);
  const [editMode, setEditMode] = useState(false);

  const lang = tweaks.lang;
  const variant = tweaks.variant;
  const t = COPY[lang];

  // Reflect variant + accent + serif toggle on <html> so CSS variables switch.
  useEffect(() => {
    document.documentElement.dataset.variant = variant;
    document.documentElement.style.setProperty("--accent", tweaks.accent);
    document.documentElement.dataset.serif = tweaks.serifAccents ? "on" : "off";
  }, [variant, tweaks.accent, tweaks.serifAccents]);

  // Keep <html lang> in sync so the browser and screen readers pick up the
  // right language for the current copy.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Persist every change (don't debounce — writes are cheap and keep
  // localStorage correct even if the tab closes immediately).
  useEffect(() => {
    saveTweaks(tweaks);
  }, [tweaks]);

  // Edit-mode handshake with the Claude Design host frame. When the host
  // activates edit mode we show the TweaksPanel and forward every mutation
  // back via postMessage so edits sync both ways.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = (e.data ?? {}) as { type?: string };
      if (d.type === "__activate_edit_mode") setEditMode(true);
      else if (d.type === "__deactivate_edit_mode") setEditMode(false);
    };
    window.addEventListener("message", onMsg);
    window.parent.postMessage({ type: "__edit_mode_available" }, "*");
    return () => window.removeEventListener("message", onMsg);
  }, []);

  function setKey<K extends TweakKey>(k: K, v: TweakValue<K>) {
    setTweaks((s) => {
      const next = { ...s, [k]: v } as Tweaks;
      window.parent.postMessage(
        { type: "__edit_mode_set_keys", edits: { [k]: v } },
        "*"
      );
      return next;
    });
  }

  const setLang = (l: Lang) => setKey("lang", l);

  return (
    <div className="app">
      <Nav lang={lang} setLang={setLang} t={t} variant={variant} />
      <main>
        <Hero t={t} />
        <Industries t={t} />
        <Products t={t} />
        <Fonio t={t} />
        <Automations t={t} />
        <Integrations t={t} />
        <ROI t={t} />
        <Cases t={t} />
        <FinalCTA t={t} />
      </main>
      <Footer t={t} />

      {editMode && <TweaksPanel tweaks={tweaks} setKey={setKey} />}
    </div>
  );
}
